import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Check, X as XIcon } from "lucide-react";
import AdminLayout from "../components/AdminLayout";
import {
  adminListCarriers,
  adminCreateCarrier,
  adminUpdateCarrier,
  adminDeleteCarrier,
} from "../lib/api";

const AdminShipping = () => {
  const [carriers, setCarriers] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [newCarrier, setNewCarrier] = useState({ slug: "", name: "", description: "", price: 0, delivery_time: "", active: true, order: 10 });
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const list = await adminListCarriers();
      setCarriers(list);
      const d = {};
      list.forEach((c) => { d[c.id] = { ...c }; });
      setDrafts(d);
    } catch {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const updateDraft = (id, field, value) => {
    setDrafts({ ...drafts, [id]: { ...drafts[id], [field]: value } });
  };

  const save = async (id) => {
    const d = drafts[id];
    try {
      await adminUpdateCarrier(id, {
        name: d.name,
        description: d.description,
        price: parseFloat(d.price),
        delivery_time: d.delivery_time,
        active: d.active,
        order: parseInt(d.order, 10) || 0,
      });
      toast.success("Transporteur mis à jour");
      await refresh();
    } catch {
      toast.error("Erreur lors de la sauvegarde");
    }
  };

  const toggleActive = async (c) => {
    try {
      await adminUpdateCarrier(c.id, { active: !c.active });
      toast.success(c.active ? "Désactivé" : "Activé");
      await refresh();
    } catch {
      toast.error("Erreur");
    }
  };

  const removeCarrier = async (c) => {
    if (!window.confirm(`Supprimer le transporteur "${c.name}" ?`)) return;
    try {
      await adminDeleteCarrier(c.id);
      toast.success("Transporteur supprimé");
      await refresh();
    } catch {
      toast.error("Erreur");
    }
  };

  const createNew = async () => {
    if (!newCarrier.slug || !newCarrier.name || !newCarrier.delivery_time) {
      toast.error("Slug, nom et délai sont requis");
      return;
    }
    try {
      await adminCreateCarrier({
        ...newCarrier,
        price: parseFloat(newCarrier.price) || 0,
        order: parseInt(newCarrier.order, 10) || 10,
      });
      toast.success("Transporteur ajouté");
      setNewCarrier({ slug: "", name: "", description: "", price: 0, delivery_time: "", active: true, order: 10 });
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  return (
    <AdminLayout title="Livraison" subtitle="Modifiez les tarifs, activez ou désactivez un transporteur, ajustez les délais. Les changements sont appliqués immédiatement sur le site.">
      {loading ? (
        <p className="text-white/50 label-caps">Chargement...</p>
      ) : (
        <>
          <div className="space-y-4 mb-16">
            {carriers.map((c) => {
              const d = drafts[c.id] || c;
              return (
                <div
                  key={c.id}
                  data-testid={`admin-carrier-${c.slug}`}
                  className={`border p-6 md:p-8 grid grid-cols-1 md:grid-cols-12 gap-4 items-start ${c.active ? "border-white/15" : "border-white/5 opacity-60"}`}
                >
                  <div className="md:col-span-3">
                    <label className="label-caps text-white/40 text-[9px] block mb-2">Nom</label>
                    <input value={d.name} onChange={(e) => updateDraft(c.id, "name", e.target.value)} className="input-line" />
                    <p className="label-caps text-white/30 text-[9px] mt-3">slug: {c.slug}</p>
                  </div>
                  <div className="md:col-span-3">
                    <label className="label-caps text-white/40 text-[9px] block mb-2">Description</label>
                    <input value={d.description || ""} onChange={(e) => updateDraft(c.id, "description", e.target.value)} className="input-line" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="label-caps text-white/40 text-[9px] block mb-2">Prix (€)</label>
                    <input type="number" step="0.10" value={d.price} onChange={(e) => updateDraft(c.id, "price", e.target.value)} className="input-line" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="label-caps text-white/40 text-[9px] block mb-2">Délai</label>
                    <input value={d.delivery_time} onChange={(e) => updateDraft(c.id, "delivery_time", e.target.value)} className="input-line" />
                  </div>
                  <div className="md:col-span-2 flex md:flex-col gap-3 md:items-end">
                    <button
                      data-testid={`toggle-${c.slug}`}
                      onClick={() => toggleActive(c)}
                      className={`label-caps text-[10px] px-4 py-2 border flex items-center gap-2 ${c.active ? "border-[color:var(--gold)] text-[color:var(--gold)]" : "border-white/20 text-white/40"}`}
                    >
                      {c.active ? <><Check size={11} /> Actif</> : <><XIcon size={11} /> Inactif</>}
                    </button>
                    <button
                      data-testid={`save-${c.slug}`}
                      onClick={() => save(c.id)}
                      className="label-caps text-[10px] text-[color:var(--gold)] border-b border-[color:var(--gold)] pb-1"
                    >
                      Enregistrer
                    </button>
                    <button
                      data-testid={`delete-${c.slug}`}
                      onClick={() => removeCarrier(c)}
                      className="text-red-400 hover:text-red-300"
                      aria-label="Supprimer"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="border border-[color:var(--gold)]/30 p-6 md:p-8">
            <p className="label-caps text-[color:var(--gold)] mb-6 flex items-center gap-2"><Plus size={12} /> Ajouter un transporteur</p>
            <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-6">
              <input placeholder="slug (ex: dpd)" className="input-line" value={newCarrier.slug} onChange={(e) => setNewCarrier({ ...newCarrier, slug: e.target.value })} data-testid="new-carrier-slug" />
              <input placeholder="Nom" className="input-line" value={newCarrier.name} onChange={(e) => setNewCarrier({ ...newCarrier, name: e.target.value })} data-testid="new-carrier-name" />
              <input placeholder="Description" className="input-line md:col-span-2" value={newCarrier.description} onChange={(e) => setNewCarrier({ ...newCarrier, description: e.target.value })} />
              <input type="number" step="0.10" placeholder="Prix" className="input-line" value={newCarrier.price} onChange={(e) => setNewCarrier({ ...newCarrier, price: e.target.value })} data-testid="new-carrier-price" />
              <input placeholder="Délai (ex: 2-3j)" className="input-line" value={newCarrier.delivery_time} onChange={(e) => setNewCarrier({ ...newCarrier, delivery_time: e.target.value })} data-testid="new-carrier-delay" />
            </div>
            <button onClick={createNew} data-testid="new-carrier-btn" className="btn-gold">Ajouter</button>
          </div>
        </>
      )}
    </AdminLayout>
  );
};

export default AdminShipping;
